import { isAfter, isValid, parseISO } from 'date-fns'

/**
 * Returns whether the cancellation limit is still in the future (after now).
 *
 * When `true`, the reservation is past its free-cancellation window and the
 * user must REQUEST a cancellation. When `false`, the reservation can be
 * canceled directly.
 *
 * The trailing `Z` is stripped so the timestamp is read as wall-clock/local
 * time, keeping this consistent across the bookings list and detail screens.
 */
export function getCancellationDeadlineIsAfterNow(
  cancellationLimit: string | null | undefined,
): boolean {
  if (!cancellationLimit) return false

  const parsed = parseISO(cancellationLimit.replace('Z', '').trim())

  return isValid(new Date(parsed))
    ? isAfter(new Date(parsed), new Date())
    : false
}

/**
 * Returns whether the free-cancellation deadline has already PASSED.
 *
 * This is the single, canonical flag used across the booking edit/cancel
 * flows (list and detail):
 *  - `true`  → the reservation is past its free-cancellation window, so the
 *              user must REQUEST an edit/cancellation (request dialog).
 *  - `false` → the reservation is still within the free window, so the edit
 *              can be done directly and the cancellation goes straight to the
 *              cancel dialog.
 *
 * Prefer this over negating `getCancellationDeadlineIsAfterNow` at call sites,
 * so the meaning of the flag is unambiguous wherever it is read.
 */
export function isPastCancellationDeadline(
  cancellationLimit: string | null | undefined,
): boolean {
  return !getCancellationDeadlineIsAfterNow(cancellationLimit)
}
